// Page with a table that shows all documents w/ number of alerts in a sortable table

import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  TableSortLabel,
  Container,
  Chip,
  Skeleton,
  IconButton,
} from '@mui/material'
import { DashboardControls } from '../charts/DashboardControls'
import { useNavigate } from 'react-router-dom'
import { useDashboardTableDocuments } from '../../../queries/hooks/useDashboardTableDocuments'
import { Refresh } from '@mui/icons-material'
import { invalidateAllQueries } from '../../../util/queryClient'
import AlertChip from './AlertChip'
import useDashboardListFilters from '../../../util/useDashboardListFilters'
import { getErrorMessage } from '../../../util/errorHandling'
import { formatDateOnly } from '../charts/_helpers'
import { useDashboardTableSort, useResetDashboardPage } from '../charts/_hooks'
import type { DashboardSortableTableOptions } from '@counsel-project/counsel-audit-ai-api'
import { getSeverityCount, getTotalSeverityCount } from '../../../util/severityCounts'
import {
  DashboardTablePagination,
  DateOfServiceCell,
  DateOfServiceHeaderCell,
  DASHBOARD_ROW_MIN_HEIGHT,
  DASHBOARD_CHIP_SLOT_HEIGHT,
} from '../dashboardListUi'

type SortableField =
  | 'document'
  | 'patient'
  | 'clinician'
  | 'date'
  | 'dateOfService'
  | 'totalAlerts'
  | 'lowAlerts'
  | 'mediumAlerts'
  | 'criticalAlerts'

type DashboardSortBy = NonNullable<DashboardSortableTableOptions['sortBy']>

const sortFieldToApiSortBy: Record<SortableField, DashboardSortBy> = {
  document: 'documentTitle',
  patient: 'patientName',
  clinician: 'clinicianName',
  date: 'documentAt',
  dateOfService: 'dateOfService',
  totalAlerts: 'totalAlerts',
  lowAlerts: 'severity1' as DashboardSortBy,
  mediumAlerts: 'severity2' as DashboardSortBy,
  criticalAlerts: 'severity3' as DashboardSortBy,
}

const BODY_ROW_HEIGHT = DASHBOARD_ROW_MIN_HEIGHT
const BODY_CELL_SX = { height: BODY_ROW_HEIGHT, py: 1 } as const
const THREE_LINE_TEXT_CLAMP_SX = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: 1.4,
  minHeight: '2.8em',
  maxHeight: '2.8em',
} as const
const DOCUMENT_TWO_LINE_SKELETON_HEIGHT_PX = 40
// TableSortLabel reserves icon space on the right, so text center sits slightly left of cell center.
const SORTABLE_HEADER_TEXT_CENTER_OFFSET_PX = -11
const CENTERED_CHIP_CELL_SX = {
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  transform: `translateX(${SORTABLE_HEADER_TEXT_CENTER_OFFSET_PX}px)`,
} as const
const CHIP_SKELETON_WIDTH = 40
const CHIP_SKELETON_HEIGHT = DASHBOARD_CHIP_SLOT_HEIGHT
const LOADING_COLUMN_KEYS = [
  'document',
  'patient',
  'clinician',
  'date',
  'dateOfService',
  'totalAlerts',
  'lowAlerts',
  'mediumAlerts',
  'criticalAlerts',
] as const

/** Columns from here on render centered alert chips rather than text. */
const FIRST_CHIP_COLUMN_INDEX = LOADING_COLUMN_KEYS.indexOf('totalAlerts')

const AllAlertsTable: React.FC = () => {
  const navigate = useNavigate()

  const { filters, resetFilters } = useDashboardListFilters()
  const { orderBy, order, handleRequestSort } = useDashboardTableSort<SortableField>('date', 'desc')

  const {
    data: masterDashboardData,
    isLoading: isDashboardDataLoading,
    error: dashboardDataError,
    currentPage,
    setCurrentPage,
    rowsPerPage,
  } = useDashboardTableDocuments({
    rowsPerPage: 10,
    sortBy: sortFieldToApiSortBy[orderBy],
    sortOrder: order,
  })
  const documentHeaderCellRef = useRef<HTMLTableCellElement | null>(null)
  const [lastKnownDocumentColumnWidth, setLastKnownDocumentColumnWidth] = useState<
    number | undefined
  >(undefined)

  useEffect(() => {
    const headerCell = documentHeaderCellRef.current
    if (!headerCell) return

    const updateWidth = () => {
      const measuredWidth = Math.round(headerCell.getBoundingClientRect().width)
      if (measuredWidth > 0) {
        setLastKnownDocumentColumnWidth(measuredWidth)
      }
    }

    updateWidth()

    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(updateWidth)
    observer.observe(headerCell)
    return () => observer.disconnect()
  }, [])

  useResetDashboardPage({ filters, setCurrentPage, resetKey: `${orderBy}:${order}` })

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setCurrentPage(newPage + 1)
  }

  // Handle row click to navigate to alerts page
  const handleRowClick = (auditId: string) => {
    navigate(`/alerts?auditId=${auditId}`)
  }

  const displayedDocuments = masterDashboardData?.results ?? []
  const totalDocuments = masterDashboardData?.pagination?.total ?? 0

  const lastRowMessage = useMemo(() => {
    if (isDashboardDataLoading) return ''
    if (dashboardDataError) {
      return getErrorMessage(dashboardDataError, 'Please try different filters or date range')
    }
    if (masterDashboardData === undefined) {
      return 'Failed to load alerts'
    }
    if (displayedDocuments.length === 0) {
      return 'No document alerts available'
    }
    return ''
  }, [isDashboardDataLoading, masterDashboardData, displayedDocuments.length, dashboardDataError])

  const renderLoadingRows = () => {
    const renderLoadingCellSkeleton = (columnIndex: number) => {
      if (columnIndex === 0) {
        return (
          <Box sx={{ ...THREE_LINE_TEXT_CLAMP_SX, width: '100%' }}>
            <Skeleton
              variant="rounded"
              width="100%"
              height={DOCUMENT_TWO_LINE_SKELETON_HEIGHT_PX}
            />
          </Box>
        )
      }

      const isChipColumn = columnIndex >= FIRST_CHIP_COLUMN_INDEX

      if (!isChipColumn) {
        return <Skeleton variant="rectangular" width="100%" height={20} />
      }

      return (
        <Box sx={CENTERED_CHIP_CELL_SX}>
          <Skeleton variant="rounded" width={CHIP_SKELETON_WIDTH} height={CHIP_SKELETON_HEIGHT} />
        </Box>
      )
    }

    return Array.from({ length: rowsPerPage }).map((_, index) => (
      <TableRow key={index} sx={{ height: BODY_ROW_HEIGHT }}>
        {LOADING_COLUMN_KEYS.map((columnKey, columnIndex) => (
          <TableCell
            key={columnKey}
            align={columnIndex >= FIRST_CHIP_COLUMN_INDEX ? 'center' : 'left'}
            sx={
              columnIndex === 0 && lastKnownDocumentColumnWidth
                ? { ...BODY_CELL_SX, minWidth: `${lastKnownDocumentColumnWidth}px` }
                : BODY_CELL_SX
            }
          >
            {renderLoadingCellSkeleton(columnIndex)}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  const handleRefresh = () => {
    resetFilters()
    invalidateAllQueries()
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* add a refresh button on right side of title */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" gutterBottom>
            All Document Alerts
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            This table shows all documents with alert counts by severity level.
          </Typography>
        </Box>
        <IconButton
          color="primary"
          onClick={handleRefresh}
          size="large"
          data-qa-id="all-alerts-reset-button"
        >
          <Refresh />
        </IconButton>
      </Box>

      <DashboardControls />

      <Paper elevation={0} sx={{ p: 3, mb: 4 }}>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {isDashboardDataLoading ? (
              <Skeleton variant="text" width={120} sx={{ display: 'inline-block' }} />
            ) : (
              `${totalDocuments} documents found`
            )}
          </Typography>
        </Box>

        <TableContainer>
          <Table aria-label="document alerts table">
            <TableHead>
              <TableRow>
                <TableCell ref={documentHeaderCellRef}>
                  <TableSortLabel
                    data-qa-id="all-alerts-sort-document"
                    active={orderBy === 'document'}
                    direction={orderBy === 'document' ? order : 'asc'}
                    onClick={() => handleRequestSort('document')}
                  >
                    Document
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    data-qa-id="all-alerts-sort-patient"
                    active={orderBy === 'patient'}
                    direction={orderBy === 'patient' ? order : 'asc'}
                    onClick={() => handleRequestSort('patient')}
                  >
                    Patient
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    data-qa-id="all-alerts-sort-clinician"
                    active={orderBy === 'clinician'}
                    direction={orderBy === 'clinician' ? order : 'asc'}
                    onClick={() => handleRequestSort('clinician')}
                  >
                    Clinician
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    data-qa-id="all-alerts-sort-date"
                    active={orderBy === 'date'}
                    direction={orderBy === 'date' ? order : 'asc'}
                    onClick={() => handleRequestSort('date')}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <DateOfServiceHeaderCell
                  qaId="all-alerts-sort-date-of-service"
                  field="dateOfService"
                  orderBy={orderBy}
                  order={order}
                  onSort={handleRequestSort}
                />
                <TableCell align="center">
                  <TableSortLabel
                    data-qa-id="all-alerts-sort-total"
                    active={orderBy === 'totalAlerts'}
                    direction={orderBy === 'totalAlerts' ? order : 'asc'}
                    onClick={() => handleRequestSort('totalAlerts')}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Total Alerts
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    data-qa-id="all-alerts-sort-low"
                    active={orderBy === 'lowAlerts'}
                    direction={orderBy === 'lowAlerts' ? order : 'asc'}
                    onClick={() => handleRequestSort('lowAlerts')}
                  >
                    Low
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    data-qa-id="all-alerts-sort-medium"
                    active={orderBy === 'mediumAlerts'}
                    direction={orderBy === 'mediumAlerts' ? order : 'asc'}
                    onClick={() => handleRequestSort('mediumAlerts')}
                  >
                    Medium
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    data-qa-id="all-alerts-sort-critical"
                    active={orderBy === 'criticalAlerts'}
                    direction={orderBy === 'criticalAlerts' ? order : 'asc'}
                    onClick={() => handleRequestSort('criticalAlerts')}
                  >
                    Critical
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isDashboardDataLoading && renderLoadingRows()}
              {!isDashboardDataLoading &&
                displayedDocuments.map((doc, index) => {
                  return (
                    <TableRow
                      key={`${doc.documentId}-${currentPage}-${index}`}
                      data-qa-id={`all-alerts-table-row-${doc.documentId}`}
                      hover
                      sx={{ cursor: 'pointer', height: BODY_ROW_HEIGHT }}
                      onClick={() => handleRowClick(doc.documentId)}
                    >
                      <TableCell sx={BODY_CELL_SX}>
                        <Box sx={THREE_LINE_TEXT_CLAMP_SX}>{doc.documentTitle}</Box>
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <Box sx={THREE_LINE_TEXT_CLAMP_SX}>{doc.patientName}</Box>
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <Box sx={THREE_LINE_TEXT_CLAMP_SX}>{doc.clinicianName}</Box>
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>{formatDateOnly(doc.documentAt)}</TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <DateOfServiceCell visitDate={doc.visitDate} />
                      </TableCell>
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        <Box sx={CENTERED_CHIP_CELL_SX}>
                          <Chip label={getTotalSeverityCount(doc)} color="default" size="small" />
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        <Box sx={CENTERED_CHIP_CELL_SX}>
                          <AlertChip severity={1} count={getSeverityCount(doc, '1')} />
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        <Box sx={CENTERED_CHIP_CELL_SX}>
                          <AlertChip severity={2} count={getSeverityCount(doc, '2')} />
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        <Box sx={CENTERED_CHIP_CELL_SX}>
                          <AlertChip severity={3} count={getSeverityCount(doc, '3')} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  )
                })}
              {lastRowMessage && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    {lastRowMessage}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <DashboardTablePagination
          count={totalDocuments}
          rowsPerPage={rowsPerPage}
          page={Math.max(0, currentPage - 1)}
          onPageChange={handleChangePage}
          isLoading={isDashboardDataLoading}
          qaId="all-alerts-pagination"
        />
      </Paper>
    </Container>
  )
}

export default AllAlertsTable
